import * as uuid from 'uuid'
import { forEach, split } from 'lodash'

export class Subscription<
    TEventTypes extends string | number,
    KEventDataTypes extends { [key in TEventTypes]: KEventDataTypes[key] },
> {
    private eventListeners: {
        [T in TEventTypes]?: {
            [subscriptionId: string]: (data: KEventDataTypes[T]) => void
        }
    } = {}

    subscribe<T extends TEventTypes>(
        eventType: T,
        eventlistener: (data: KEventDataTypes[T]) => void
    ) {
        const subscriptionId: string = uuid.v4()
        this.eventListeners[eventType] = {
            ...this.eventListeners[eventType],
            [subscriptionId]: eventlistener,
        }
        return eventType + '_' + subscriptionId
    }

    publishUpdate(eventType: TEventTypes, data?: KEventDataTypes[TEventTypes]) {
        forEach(
            this.eventListeners[eventType],
            (listener: (data: KEventDataTypes[TEventTypes]) => void) =>
                listener(data as KEventDataTypes[TEventTypes])
        )
    }

    unsubscribe(subscriptionEventId: string) {
        const [eventType, subscriptionId] = split(subscriptionEventId, '_') as [TEventTypes, string]
        delete this.eventListeners[eventType]?.[subscriptionId]
    }

    unsubscribeFromAll = () => {
        this.eventListeners = {}
    }

    /**
     * Subscribe to multiple events with their corresponding listeners.
     *
     * @param {Array<{ eventType: T, listener: (data?: KEventDataTypes) => void }>} events - An array of objects containing the eventType and listener for each event.
     * @return {Array<number>} An array of subscription IDs for each event.
     */
    subscribeMany<T extends TEventTypes>(
        events: {
            eventType: T
            listener: (data?: KEventDataTypes[T]) => void
        }[]
    ) {
        const subscriptionIds = events.map(event => {
            return this.subscribe(event.eventType, event.listener)
        })
        return subscriptionIds
    }

    /**
     * Unsubscribes from multiple subscriptions.
     *
     * @param {string[]} subscriptionIds - An array of subscription IDs.
     */
    unsubscribeMany(subscriptionIds: string[]) {
        subscriptionIds.forEach(subscriptionId => {
            this.unsubscribe(subscriptionId)
        })
    }
}
